import { NavLink } from "react-router-dom";
import { PlateSearch } from "./PlateSearch";
import { USER_ROLE_LABELS } from "@bi/types";
import { useAuth } from "../lib/auth";
import { useMobileLayout } from "../features/maintenance-mobile/useMobileLayout";
import { SIDEBAR_WIDTH, useSidebarCollapsed } from "../lib/sidebar";
import {
  IconHome, IconDollar, IconTruck, IconLineChart,
  IconCashflow, IconLightbulb, IconDocument, IconWhatsApp,
  IconShield, IconSync, IconCog, IconSparkle, IconFuel, IconTire, IconWrench, IconUser, IconClipboard, IconCalendar, IconSmartphone,
  IconChevronLeft, IconChevronRight, IconSearch, IconLogout,
} from "./icons";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  end?: boolean;
}

const MAIN_NAV: NavItem[] = [
  { to: "/",              label: "Visão Executiva",   icon: IconHome,        end: true },
  { to: "/financeiro",    label: "Financeiro",         icon: IconDollar },
  { to: "/operacional",   label: "Operacional CT-e",   icon: IconTruck },
  { to: "/dre",           label: "DRE / Resultado",    icon: IconLineChart },
  { to: "/fluxo-caixa",   label: "Fluxo de Caixa",    icon: IconCashflow },
];

const FROTA_NAV: NavItem[] = [
  { to: "/frota",             label: "Visão Geral", icon: IconTruck, end: true },
  { to: "/frota/combustivel", label: "Combustível", icon: IconFuel },
  { to: "/frota/pneus",       label: "Pneus",       icon: IconTire },
  { to: "/frota/custo-placa", label: "Custo por Placa",  icon: IconDollar },
  { to: "/frota/margem",      label: "Margem por Placa", icon: IconLineChart },
  { to: "/frota/pedagio",     label: "Pedágio",     icon: IconDollar },
  { to: "/frota/seguro",      label: "Seguro de Carga",  icon: IconShield },
  { to: "/frota/motoristas",  label: "Motoristas",  icon: IconUser },
];

const MANUTENCAO_NAV: NavItem[] = [
  { to: "/frota/manutencao", label: "Central de Manutenção", icon: IconWrench, end: true },
  { to: "/frota/manutencao/preventiva", label: "Manutenção Preventiva", icon: IconCalendar },
  { to: "/frota/manutencao/ordens", label: "Ordens de Serviço", icon: IconClipboard },
  { to: "/frota/manutencao/erp", label: "OS do ERP", icon: IconWrench },
  { to: "/frota/manutencao/checklists", label: "Checklists", icon: IconDocument },
  { to: "/frota/manutencao/nao-conformidades", label: "Não Conformidades", icon: IconShield },
  { to: "/frota/manutencao/modelos", label: "Modelos de Checklist", icon: IconClipboard },
  { to: "/frota/manutencao/grupos", label: "Grupos de Veículos", icon: IconTruck },
];

const INTEL_NAV: NavItem[] = [
  { to: "/inteligencia",  label: "Insights e Alertas", icon: IconLightbulb },
];

const CHANNEL_NAV: NavItem[] = [
  { to: "/whatsapp",      label: "WhatsApp",           icon: IconWhatsApp },
];

/** Na demonstração não há canal de WhatsApp: a conversa roda pela tela de perguntas (mesmo motor, sem envio). */
const CHANNEL_NAV_DEMO: NavItem[] = [
  { to: "/perguntas", label: "Perguntar ao SIAG", icon: IconSparkle },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/governanca", label: "Governança", icon: IconShield },
  { to: "/qualidade-dados", label: "Qualidade dos Dados", icon: IconShield },
  { to: "/sincronizacao",   label: "Sincronização",        icon: IconSync },
  { to: "/configuracoes",   label: "Configurações",        icon: IconCog },
];

/** Na empresa de demonstração só a leitura de qualidade dos dados fica visível;
 *  sincronização e configurações expõem integrações (a API também nega). */
const ADMIN_NAV_DEMO: NavItem[] = ADMIN_NAV.filter((i) => i.to === "/qualidade-dados");

function NavGroup({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  return (
    <nav className="space-y-0.5">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          // Recolhido, o nome sai da tela: fica no title (dica ao passar o mouse)
          // e no aria-label (leitor de tela).
          title={collapsed ? item.label : undefined}
          aria-label={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            `relative flex items-center rounded-lg text-sm transition-colors group ${
              collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
            } ${
              isActive
                ? "bg-tm-500/20 text-tm-100 font-medium"
                : "text-tm-100/60 hover:bg-white/5 hover:text-tm-50"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-tm-400" : "text-tm-100/50 group-hover:text-tm-100/80"}`} />
              {!collapsed && <span className="truncate">{item.label}</span>}
              {item.badge !== undefined && (collapsed ? (
                <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-tm-500" aria-hidden="true" />
              ) : (
                <span className="ml-auto bg-tm-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                  {item.badge}
                </span>
              ))}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/** Título da seção; recolhido vira só um traço separando os grupos. */
function SectionTitle({ children, collapsed }: { children: string; collapsed: boolean }) {
  if (collapsed) return <div className="mx-3 mb-2 h-px bg-white/10" role="separator" aria-label={children} />;
  return <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-tm-100/30">{children}</p>;
}

export function Sidebar() {
  const { user, signOut } = useAuth();
  const maintenanceOnly = user?.role === "manutencao";
  const mobile = useMobileLayout();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const width = collapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.open;
  const toggleButton = (
    <button
      type="button"
      onClick={() => setCollapsed(!collapsed)}
      aria-expanded={!collapsed}
      aria-controls="menu-lateral"
      aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
      title={collapsed ? "Expandir menu" : "Recolher menu"}
      className="grid place-items-center w-8 h-8 rounded-lg text-tm-100/60 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-400 flex-shrink-0"
    >
      {collapsed ? <IconChevronRight className="w-4 h-4" /> : <IconChevronLeft className="w-4 h-4" />}
    </button>
  );
  return (
    <aside id="menu-lateral" aria-label="Menu principal"
      className={`fixed inset-y-0 left-0 ${width.aside} bg-tm-900 flex flex-col z-40 select-none transition-[width] duration-200`}>
      {/* Logo + recolher */}
      <div className={`border-b border-white/10 ${collapsed ? "px-2 py-4 flex flex-col items-center gap-3" : "px-4 py-5"}`}>
        {collapsed ? (
          <>
            <div className="w-9 h-9 rounded-lg bg-tm-500 grid place-items-center flex-shrink-0" title={user?.isDemo && user.companyName ? user.companyName : "Transmasi"}>
              <IconSparkle className="w-5 h-5 text-white" />
            </div>
            {toggleButton}
          </>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-tm-500 grid place-items-center flex-shrink-0">
              <IconSparkle className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-white leading-tight truncate">{user?.isDemo && user.companyName ? user.companyName.toUpperCase() : "TRANSMASI"}</div>
              <div className="text-xs text-tm-300 leading-tight mt-0.5">Inteligência de Negócios</div>
            </div>
            {toggleButton}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className={`flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-5 ${collapsed ? "px-2" : "px-3"}`}>
        {!maintenanceOnly && <>
        <div>
          <SectionTitle collapsed={collapsed}>Painéis</SectionTitle>
          <NavGroup items={MAIN_NAV} collapsed={collapsed} />
        </div>

        <div>
          <SectionTitle collapsed={collapsed}>Frota</SectionTitle>
          {collapsed ? (
            <button type="button" onClick={() => setCollapsed(false)} title="Buscar placa (abre o menu)" aria-label="Buscar placa"
              className="mb-1 flex w-full justify-center rounded-lg py-2.5 text-tm-100/50 hover:bg-white/5 hover:text-tm-100/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-400">
              <IconSearch className="w-5 h-5" />
            </button>
          ) : <PlateSearch />}
          <NavGroup items={FROTA_NAV} collapsed={collapsed} />
        </div>

        </>}
        <div>
          <SectionTitle collapsed={collapsed}>Manutenção</SectionTitle>
          <NavGroup items={MANUTENCAO_NAV} collapsed={collapsed} />
        </div>

        {!maintenanceOnly && <>
        <div>
          <SectionTitle collapsed={collapsed}>Inteligência</SectionTitle>
          <NavGroup items={INTEL_NAV} collapsed={collapsed} />
        </div>

        <div>
          <SectionTitle collapsed={collapsed}>Canal</SectionTitle>
          <NavGroup items={user?.isDemo ? CHANNEL_NAV_DEMO : CHANNEL_NAV} collapsed={collapsed} />
        </div>

        <div>
          <SectionTitle collapsed={collapsed}>Sistema</SectionTitle>
          <NavGroup items={user?.isDemo ? ADMIN_NAV_DEMO : ADMIN_NAV.filter(item => item.to !== "/governanca" || user?.role === "admin")} collapsed={collapsed} />
        </div>
        </>}
      </div>

      {/* Footer */}
      {collapsed ? (
        <div className="px-2 py-3 border-t border-white/10 flex flex-col items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-tm-500/20 border border-tm-500/50 grid place-items-center text-sm font-semibold text-tm-100"
            title={`${user?.name ?? ""}${user ? ` · ${USER_ROLE_LABELS[user.role]}` : ""}`} aria-label={user?.name ?? "Usuário"}>
            {(user?.name ?? "?").trim().charAt(0).toUpperCase()}
          </div>
          {mobile.available && (
            <button onClick={() => mobile.switchTo("mobile")} title="Versão mobile" aria-label="Versão mobile"
              className="grid place-items-center w-8 h-8 rounded-lg text-tm-100/60 hover:bg-white/10 hover:text-white">
              <IconSmartphone className="h-4 w-4" />
            </button>
          )}
          <button onClick={signOut} title="Sair" aria-label="Sair"
            className="grid place-items-center w-8 h-8 rounded-lg text-tm-100/60 hover:bg-white/10 hover:text-white">
            <IconLogout className="h-4 w-4" />
          </button>
        </div>
      ) : (
      <div className="px-4 py-3 border-t border-white/10">
        {user?.isDemo && (
          <p className="mb-2 inline-block rounded bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-tm-100/70">
            Ambiente de Demonstração
          </p>
        )}
        <div className="mb-4 border-b border-white/10 pb-3">
          <p className="truncate text-sm font-medium text-white">{user?.name}</p>
          <p className="mt-1 truncate text-xs text-tm-100/60">{user?.email}</p>
          <span className="mt-2 inline-flex rounded-full border border-tm-500/50 bg-tm-500/10 px-2 py-0.5 text-xs font-medium text-tm-200">{user ? USER_ROLE_LABELS[user.role] : ""}</span>
          <button onClick={signOut} className="ml-3 text-xs text-tm-100/70 hover:text-white">Sair</button>
          {mobile.available && (
            <button onClick={() => mobile.switchTo("mobile")} className="mt-3 flex w-full items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-tm-100/80 hover:bg-white/5 hover:text-white">
              <IconSmartphone className="h-4 w-4" /> Versão mobile
            </button>
          )}
        </div>
        <p className="text-xs text-tm-100/30 leading-tight">Powered by</p>
        <p className="text-xs text-tm-100/50 font-medium">Sistólica · Inteligência SIAG</p>
      </div>
      )}
    </aside>
  );
}
